import { Theme, Components } from '@mui/material/styles';

// Bold Monochrome Theme Overrides for MUI
export const boldMonochromeComponents: Components<Theme> = {
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '12px',
        border: '2px solid',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(0, 200, 224, 0.2)' : '#e5e5e5',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 0 12px rgba(0, 200, 224, 0.1)'
          : 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 0 20px rgba(0, 200, 224, 0.3), 0 0 40px rgba(0, 200, 224, 0.15)'
            : 'none',
        },
      }),
      elevation1: ({ theme }) => ({
        boxShadow: theme.palette.mode === 'dark'
          ? '0 0 15px rgba(0, 200, 224, 0.15), 0 4px 20px rgba(0, 0, 0, 0.4)'
          : '0 4px 20px rgba(0, 0, 0, 0.08)',
      }),
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '16px',
        border: '2px solid',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(0, 200, 224, 0.3)' : '#e5e5e5',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 0 15px rgba(0, 200, 224, 0.15), inset 0 0 10px rgba(0, 200, 224, 0.03)'
          : 'none',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'visible',
        '&:hover': {
          borderColor: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
          transform: 'translateY(-2px)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 0 25px rgba(0, 200, 224, 0.4), 0 0 50px rgba(0, 200, 224, 0.2), 0 12px 32px rgba(0, 200, 224, 0.15)'
            : '0 12px 32px rgba(0, 0, 0, 0.1)',
        },
      }),
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '24px',
        '&:last-child': {
          paddingBottom: '24px',
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        fontWeight: 600,
        textTransform: 'none',
        padding: '10px 24px',
        transition: 'all 0.15s ease',
      },
      contained: ({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
        color: theme.palette.mode === 'dark' ? '#0a0a0a' : '#ffffff',
        border: '2px solid',
        borderColor: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 0 10px rgba(0, 200, 224, 0.3), 0 0 20px rgba(0, 200, 224, 0.15)'
          : 'none',
        '&:hover': {
          backgroundColor: theme.palette.mode === 'dark' ? '#41e2f7' : '#000000',
          borderColor: theme.palette.mode === 'dark' ? '#41e2f7' : '#000000',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 0 20px rgba(0, 200, 224, 0.5), 0 0 40px rgba(0, 200, 224, 0.3), 0 4px 16px rgba(0, 200, 224, 0.3)'
            : '0 4px 16px rgba(0, 0, 0, 0.2)',
          transform: 'translateY(-1px)',
        },
      }),
      outlined: ({ theme }) => ({
        borderWidth: '2px',
        borderColor: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
        color: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 0 8px rgba(0, 200, 224, 0.2)'
          : 'none',
        '&:hover': {
          borderWidth: '2px',
          backgroundColor: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
          color: theme.palette.mode === 'dark' ? '#0a0a0a' : '#ffffff',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 0 15px rgba(0, 200, 224, 0.4), 0 0 30px rgba(0, 200, 224, 0.2)'
            : 'none',
        },
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '6px',
        fontWeight: 600,
        fontSize: '0.75rem',
        letterSpacing: '0.03em',
        border: '1px solid',
      }),
      colorSuccess: ({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(0, 200, 224, 0.15)'
          : '#dcfce7',
        color: theme.palette.mode === 'dark' ? '#00c8e0' : '#166534',
        borderColor: theme.palette.mode === 'dark'
          ? 'rgba(0, 200, 224, 0.3)'
          : '#86efac',
      }),
      colorWarning: ({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(251, 191, 36, 0.15)'
          : '#fef9c3',
        color: theme.palette.mode === 'dark' ? '#fbbf24' : '#854d0e',
        borderColor: theme.palette.mode === 'dark'
          ? 'rgba(251, 191, 36, 0.3)'
          : '#fde047',
      }),
      colorError: ({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(239, 68, 68, 0.15)'
          : '#fee2e2',
        color: theme.palette.mode === 'dark' ? '#ef4444' : '#991b1b',
        borderColor: theme.palette.mode === 'dark'
          ? 'rgba(239, 68, 68, 0.3)'
          : '#fca5a5',
      }),
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '12px',
        border: '2px solid',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(0, 200, 224, 0.25)' : '#e5e5e5',
        overflow: 'hidden',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 0 15px rgba(0, 200, 224, 0.1), inset 0 0 10px rgba(0, 200, 224, 0.03)'
          : 'none',
      }),
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
        '& .MuiTableCell-head': {
          fontWeight: 700,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: theme.palette.mode === 'dark' ? '#a3a3a3' : '#525252',
          borderBottom: '2px solid',
          borderColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#e5e5e5',
        },
      }),
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        transition: 'background 0.15s ease',
        '&:hover': {
          backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fafafa',
        },
        '&:last-child td': {
          borderBottom: 'none',
        },
      }),
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#e5e5e5',
        padding: '16px 20px',
      }),
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: ({ theme }) => ({
        height: 8,
        borderRadius: 4,
        backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#e5e5e5',
      }),
      bar: ({ theme }) => ({
        borderRadius: 4,
        backgroundColor: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
      }),
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          transition: 'box-shadow 0.2s ease',
          '& fieldset': {
            borderWidth: '2px',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(0, 200, 224, 0.2)' : '#e5e5e5',
          },
          '&:hover fieldset': {
            borderColor: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
          },
          '&:hover': {
            boxShadow: theme.palette.mode === 'dark'
              ? '0 0 10px rgba(0, 200, 224, 0.2)'
              : 'none',
          },
          '&.Mui-focused fieldset': {
            borderColor: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
            borderWidth: '2px',
          },
          '&.Mui-focused': {
            boxShadow: theme.palette.mode === 'dark'
              ? '0 0 15px rgba(0, 200, 224, 0.3), 0 0 30px rgba(0, 200, 224, 0.15)'
              : 'none',
          },
        },
      }),
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '8px',
        transition: 'box-shadow 0.2s ease',
        '& .MuiOutlinedInput-notchedOutline': {
          borderWidth: '2px',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(0, 200, 224, 0.2)' : '#e5e5e5',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
        },
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark'
            ? '0 0 10px rgba(0, 200, 224, 0.2)'
            : 'none',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
          borderWidth: '2px',
        },
        '&.Mui-focused': {
          boxShadow: theme.palette.mode === 'dark'
            ? '0 0 15px rgba(0, 200, 224, 0.3), 0 0 30px rgba(0, 200, 224, 0.15)'
            : 'none',
        },
      }),
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '8px',
        border: '2px solid',
      }),
      standardInfo: ({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(0, 180, 216, 0.1)'
          : '#eff6ff',
        borderColor: theme.palette.mode === 'dark'
          ? 'rgba(0, 180, 216, 0.3)'
          : '#93c5fd',
        color: theme.palette.mode === 'dark' ? '#00b4d8' : '#1e40af',
      }),
      standardSuccess: ({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(0, 200, 224, 0.1)'
          : '#dcfce7',
        borderColor: theme.palette.mode === 'dark'
          ? 'rgba(0, 200, 224, 0.3)'
          : '#86efac',
        color: theme.palette.mode === 'dark' ? '#00c8e0' : '#166534',
      }),
      standardWarning: ({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(251, 191, 36, 0.1)'
          : '#fef9c3',
        borderColor: theme.palette.mode === 'dark'
          ? 'rgba(251, 191, 36, 0.3)'
          : '#fde047',
        color: theme.palette.mode === 'dark' ? '#fbbf24' : '#854d0e',
      }),
      standardError: ({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(239, 68, 68, 0.1)'
          : '#fee2e2',
        borderColor: theme.palette.mode === 'dark'
          ? 'rgba(239, 68, 68, 0.3)'
          : '#fca5a5',
        color: theme.palette.mode === 'dark' ? '#ef4444' : '#991b1b',
      }),
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#e5e5e5',
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '8px',
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(0, 200, 224, 0.1)'
            : 'rgba(23, 23, 23, 0.08)',
        },
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiTabs-indicator': {
          backgroundColor: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontWeight: 600,
        textTransform: 'none',
        '&.Mui-selected': {
          color: theme.palette.mode === 'dark' ? '#00c8e0' : '#171717',
        },
      }),
    },
  },
};

// Grid Background CSS for inline use
export const gridBackgroundLight = `
  background-color: #fafafa;
  background-image:
    linear-gradient(rgba(0, 0, 0, 0.08) 2px, transparent 2px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.08) 2px, transparent 2px);
  background-size: 40px 40px;
  background-position: -1px -1px;
`;

export const gridBackgroundBoldLight = `
  background-color: #f5f5f5;
  background-image:
    linear-gradient(rgba(0, 0, 0, 0.12) 2px, transparent 2px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.12) 2px, transparent 2px);
  background-size: 48px 48px;
  background-position: -1px -1px;
`;

export const gridBackgroundDark = `
  background-color: #0a0a0a;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.06) 2px, transparent 2px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.06) 2px, transparent 2px);
  background-size: 40px 40px;
  background-position: -1px -1px;
`;

export const gridBackgroundBoldDark = `
  background-color: #0a0a0a;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.1) 2px, transparent 2px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.1) 2px, transparent 2px);
  background-size: 48px 48px;
  background-position: -1px -1px;
`;

export default boldMonochromeComponents;
